import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, redirect } from 'react-router';
import { EntityPermissionType } from '@wavingroup/iot-core/gen/ts/wavin/kleos/v1/permission_pb';
import { listDeviceAccessKeysQuery } from '~/pages/Device/api/listDeviceAccessKeys.queries';
import { DeviceAccessKeysListModel } from '~/pages/Device/models/DeviceAccessKeysListModel';
import { createDeviceRemoteConnectionRoute } from '~/routes';
import { ENABLE_KLEOS_ROLES } from '~/services/environment/environment';
import { myDevicePermissionsQuery } from '~/shared/api/myDevicePermissions.queries';
import { checkAuth } from '~/shared/auth/auth-utils';
import { createNameFromDeviceId } from '~/shared/models/id-utils';
import { assertIsNonBlankString } from '~/types/assert-type';

export type DeviceAccessKeysLoaderData = DeviceAccessKeysListModel;

const hasUserManagementPermission = async (
  queryClient: QueryClient,
  deviceName: string,
) => {
  try {
    const permissions = await queryClient.ensureQueryData(
      myDevicePermissionsQuery({ deviceName }),
    );
    return permissions.includes(EntityPermissionType.USER_MANAGEMENT);
  } catch {
    return false;
  }
};

export const deviceAccessKeysLoader =
  (queryClient: QueryClient) =>
  async ({ params, request }: LoaderFunctionArgs) => {
    await checkAuth(request);

    const { deviceId } = params;

    assertIsNonBlankString(deviceId);

    const deviceName = createNameFromDeviceId(deviceId);

    if (
      ENABLE_KLEOS_ROLES &&
      !(await hasUserManagementPermission(queryClient, deviceName))
    ) {
      return redirect(createDeviceRemoteConnectionRoute(deviceName));
    }

    return await queryClient.ensureQueryData(
      listDeviceAccessKeysQuery({ deviceId }),
    );
  };
